/* eslint-disable no-empty */
// src/embed/widget-booking-form.jsx
import React, { Component } from "react";
import { createRoot } from "react-dom/client";
import "./widget-styles.scss";
import BookingForm from "../components/Forms/BookAppointmentForm/BookAppointmentFrom";
import { MemoryRouter } from "react-router-dom";

// Hosts that actually serve /.netlify/functions/api-proxy. Optimizers like
// NitroPack rehost the widget JS on the WordPress origin while keeping the
// original tools host inside the rewritten path, so match a known host
// anywhere in the src rather than trusting the URL's origin.
const TOOLS_HOSTS = ["tools.unclekam.com", "tools-unclekam.netlify.app"];

function toolsOriginFromSrc(src) {
  if (!src) return "";
  try {
    const u = new URL(src, location.href);
    if (TOOLS_HOSTS.includes(u.hostname)) return u.origin;
  } catch {}
  for (const host of TOOLS_HOSTS) {
    if (src.includes(`/${host}/`) || src.includes(`//${host}/`)) {
      return `https://${host}`;
    }
  }
  return "";
}

function detectToolsOrigin() {
  const fromCur = toolsOriginFromSrc(document.currentScript && document.currentScript.src);
  if (fromCur) return fromCur;
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const fromScan = toolsOriginFromSrc(scripts[i].src || "");
    if (fromScan) return fromScan;
  }
  const isQA = new URLSearchParams(location.search).has("qa_mode");
  return isQA ? "https://tools-unclekam.netlify.app" : "https://tools.unclekam.com";
}

const toCamel = (s) => s.replace(/[-_](\w)/g, (_, c) => c.toUpperCase());
const parse = (v) =>
  v == null ? undefined : v === "" ? true : v === "true" ? true : v === "false" ? false : v;

class ErrorBoundary extends Component {
  constructor(p){ super(p); this.state = { error: null }; }
  static getDerivedStateFromError(err){ return { error: err }; }
  componentDidCatch(err, info){ console.error("[booking-form] component error:", err, info); }
  render(){
    if (this.state.error){
      return (
        <div style={{ padding: 12, border: "2px solid #f33", background: "#fee" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Widget crashed</div>
          <code style={{ fontSize: 12 }}>{String(this.state.error)}</code>
        </div>
      );
    }
    return this.props.children;
  }
}

function register(tagName, Comp, { attrToProp = {} } = {}) {
  if (customElements.get(tagName)) return;

  class ReactElement extends HTMLElement {
    static get observedAttributes() {
      const keys = Object.keys(attrToProp);
      // no functions-origin — we auto-detect it now
      return keys.length ? keys.map((k) => k.toLowerCase()) : ["theme","qa_mode","data_source"];
    }

    _propsFromAttrs() {
      const props = {};
      if (Object.keys(attrToProp).length){
        for (const [attr, prop] of Object.entries(attrToProp)){
          const v = this.getAttribute(attr) ?? this.getAttribute(attr.toLowerCase());
          props[prop] = parse(v);
        }
      } else {
        for (const attr of this.getAttributeNames()){
          props[toCamel(attr)] = parse(this.getAttribute(attr));
        }
      }
      return props;
    }

    _render() {
      if (!this._root) return;
      const p = this._propsFromAttrs();

      const origin = detectToolsOrigin();
      const proxyUrl = `${origin}/.netlify/functions/api-proxy`;

      // global fallback for any component that forgets to pass the prop
      window.__UK_PROXY_URL__ = proxyUrl;

      const app = (
        <ErrorBoundary>
          <MemoryRouter>
            <BookingForm {...p} proxyUrl={proxyUrl} />
          </MemoryRouter>
        </ErrorBoundary>
      );

      this._root.render(app);
    }

    connectedCallback(){
      const m = document.createElement("div");
      this.appendChild(m);
      this._root = createRoot(m);
      this._render();
    }
    attributeChangedCallback(){ this._render(); }
    disconnectedCallback(){ this._root?.unmount(); this._root = null; }
  }

  customElements.define(tagName, ReactElement);
}

register("appointment-booking-form", BookingForm, {
  attrToProp: {
    qa_mode: "qa_mode",
    redirectUrl: "redirectUrl",
    auto_login: "auto-login",
  },
});
